import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useRef,
} from 'react'
import styled from 'styled-components'

import { Inline, get, TextHeading, Button, Icon } from '@ally/metronome-ui'
import { muiSearchTransaction, muiDownload } from '@ally/metronome-icons'
import { NonPrintingWrapper } from '../../../utils/NonPrintingWrapper'
import { DrawerState } from '../../Checking/SpendingBuckets'

const StyledInline = styled(Inline)`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
`
const StyledTextHeading = styled(TextHeading)`
  margin-bottom: ${get.space('xs')};
`
const StyledSpan = styled('span')`
  margin-right: ${get.space('xxs')};
  padding-left: ${get.space('sm')};
`

interface TransactionHistoryHeadingProps {
  onClose: () => void
  headerRef: MutableRefObject<HTMLHeadingElement | null>
  transactionHistoryDrawer: DrawerState
  setTransactionHistoryDrawer: Dispatch<SetStateAction<DrawerState>>
}

export const TransactionHistoryHeading = ({
  onClose,
  headerRef,
  transactionHistoryDrawer: { selected, isHidden },
  setTransactionHistoryDrawer,
}: TransactionHistoryHeadingProps): React.ReactElement => {
  const searchButtonRef = useRef<HTMLButtonElement>(null)
  const downloadButtonRef = useRef<HTMLButtonElement>(null)

  const isSearchSelected = selected === 'search' && !isHidden
  const isDownloadSelected = selected === 'download' && !isHidden

  const onDrawerTriggerClick = (drawerOption: 'search' | 'download'): void => {
    if (!isHidden && drawerOption === selected) {
      onClose()
      setTransactionHistoryDrawer({ selected: '', isHidden: true })
    } else {
      setTransactionHistoryDrawer({ selected: drawerOption, isHidden: false })
    }
  }

  return (
    <StyledInline space="none">
      <StyledTextHeading tag="h2" size="sm" tabIndex={-1} ref={headerRef}>
        Transaction History
      </StyledTextHeading>
      <NonPrintingWrapper>
        <StyledInline space="none">
          <Button
            variant="link"
            unsupportedContent={
              <span aria-label="Search Transactions">
                <StyledSpan>
                  <Icon icon={muiSearchTransaction} size="sm" ariaHidden />
                </StyledSpan>{' '}
                Search
              </span>
            }
            onClick={(): void => onDrawerTriggerClick('search')}
            ref={searchButtonRef}
            aria-expanded={isSearchSelected}
            data-testid="close-search-drawer-button"
            withAllyTM="transactionHistory-search"
          />
          <Button
            variant="link"
            unsupportedContent={
              <span aria-label="Download Transactions">
                <StyledSpan>
                  <Icon icon={muiDownload} size="sm" ariaHidden />
                </StyledSpan>{' '}
                Download
              </span>
            }
            ref={downloadButtonRef}
            onClick={(): void => onDrawerTriggerClick('download')}
            aria-expanded={isDownloadSelected}
            data-testid="download-button"
            withAllyTM="transactionHistory-download"
          />
        </StyledInline>
      </NonPrintingWrapper>
    </StyledInline>
  )
}
